import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import '../Styles/PrivacyPolicy.css';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="privacy-policy-page">
      {/* Header Navbar */}
      <header className="policy-navbar">
        <div className="navbar-container">
          <div className="navbar-logo" onClick={() => navigate('/')}>
            <img src="/Images/noteforge-logo.png" alt="NoteForge Logo" className="nav-logo-img" />
            <span className="nav-logo-text">NoteForge</span>
          </div>
        </div>
      </header>

      <div className="privacy-policy-card">
        <div className="privacy-policy-header">
          <h1>Privacy Policy</h1>
          <div className="header-divider"></div>
          <div className="header-meta">
            <div className="effective-date">
              <span className="date-label">Effective</span>
              <span className="date-value">January 22, 2026</span>
            </div>
            <div className="see-also">
              <span>See also:</span>
              <button onClick={() => navigate('/terms-of-service')} className="see-also-btn">Terms of Service</button>
            </div>
          </div>
        </div>
        <div className="privacy-policy-content">

          <div className="privacy-intro">
            <p>
              This Privacy Policy ("Policy") describes how Noteforge LLC ("NoteForge," "we," "us," or "our") collects, uses, stores, and shares your information when you use our Services. For the purpose of this Policy, "Personal Information" means any information relating to an identified or identifiable individual. This includes Personal Information you provide or generate when you use: (a) Our NoteForge meeting assistant app (the "App") for web, Android, and iOS; and (b) <a href="https://noteforge.ai" target="_blank" rel="noopener noreferrer">https://noteforge.ai</a>, and any other dedicated noteforge.ai websites (the "Website") (collectively, the "Services").
            </p>
            <p>
              Noteforge LLC is the data controller for your Personal Information, with offices at:
            </p>
            <p className="address-block">
              <strong>2nd floor, S3, Road No. 36, Jawahar Colony, Jubilee Hills, Hyderabad, Telangana 500033</strong>
            </p>
            <p>
              If you have questions about this Privacy Policy, please contact us at <a href="mailto:support@noteforge.ai">support@noteforge.ai</a>.
            </p>
            <p>
              Where we have a NoteForge Business or enterprise service agreement in place with an enterprise customer who is asking you to use our Services (for example, your employer), we obtain and process your Personal Information on behalf of and at the instructions of that customer. In that context, such enterprise customers are the data controllers and their privacy policies will apply to the processing of your Information. We encourage you to read their privacy policies.
            </p>
            <p>
              When you use the Services, you accept and understand that we collect, process, use, and store your Personal Information as described in this Policy. If you do not agree with this Policy, you must not access or use the Services.
            </p>
          </div>

          <section className="privacy-section">
            <h2>1. Information We Collect</h2>
            <p>We collect the following types of information from you when you use the Services.</p>
            
            <h3>Information You Provide to Us</h3>
            <ul className="bullet-list">
              <li>
                <strong>Registration Information.</strong> When you create an account on our Services, you will be asked to provide your name, email, and a password. For Pro or Business plans which are paid Services, our payment processing partner Stripe, Inc. may also collect your name, billing address, and payment information. Payment information is not shared with us and is maintained by Stripe.
              </li>
              <li>
                <strong>App Information.</strong> When you use the Services, you may provide us with your audio recordings ("Audio Recordings"), manual text provided in the context of the Services.
              </li>
              <li>
                <strong>Communication Information.</strong> When you contact us, you provide us with your phone number, email, and any other information you choose to provide over such communication, including information about your query.
              </li>
            </ul>

            <h3>Information You Provide About Others</h3>
            <p>
              Currently, our application does not allow users to refer others or collaborate by sharing the contact information (e.g., email addresses) of friends or co-workers. If user-provided content contains Personal Information of third parties, you are responsible for ensuring that you have the necessary permissions to share such information with us.
            </p>

            <h3>Information We Automatically Collect or Generate</h3>
            <p>When you use the Services, we automatically collect or generate the following information about you:</p>
            <ul className="bullet-list">
              <li>
                <strong>Usage Information.</strong> Information pertaining to your use, including timestamps, such as access, record, share, edit and delete events, app use information, and transaction records.
              </li>
              <li>
                <strong>Device Information.</strong> We assign a unique user identifier ("UUID") to each mobile device that accesses the Services. When you use our Services, you provide information such as your IP address, UUIDs, device IDs, web beacons and other device information (such as carrier type, whether you access our Service from a desktop or mobile platform, device model, brand, web browser and operating system).
              </li>
              <li>
                <strong>Location Information.</strong> When you use the Services, we receive your approximate location information. For example, we infer your approximate location by using your IP address.
              </li>
            </ul>

            <h3>Information Received from Third Parties</h3>
            <ul className="bullet-list">
              <li>
                <strong>Information from Platforms Our Services Relies On.</strong> We receive transaction information from our payment processor Stripe. Currently, we do not access any data from third-party platforms via integrations or OAuth logins.
              </li>
            </ul>

            <h3>Aggregated Data</h3>
            <p>
              Currently, we do not collect or use aggregated data. In the future, we may collect and use aggregated data such as statistical or demographic data for our purposes. Aggregated data may be derived from your Personal Information but is not Personal Information as this data will not directly or indirectly reveal your identity. However, if we combine or connect aggregated data with your Personal Information so that it can directly or indirectly identify you, we will treat the combined data as Personal Information which will be used in accordance with this Policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. How We Use Your Personal Information</h2>
            <p>We use your Personal Information for the following purposes:</p>
            <ul className="bullet-list">
              <li>
                <strong>Set Up Your Account.</strong> We use your registration information like name and email, and device information in order to set up an account for you to use our Services. We do so in accordance with our contractual and precontractual obligations to you in order to provide you with an account to use the Services.
              </li>
              <li>
                <strong>Provide You with the Services.</strong> We use your audio recordings, usage information, and platform information in order to provide you with the Services. Our transcription services are currently available only in English. In addition, we use your communication information to facilitate support through sending emails (e.g., retrieval of a forgotten password or confirmation of payment). We do so in accordance with our contractual obligations to you in order to provide you with the Services. If your subscription expires, you will retain access to view your existing transcriptions and notes, but you will not be able to create new conversations or transcriptions until you renew your subscription.
              </li>
              <li>
                <strong>Improve and Monitor the Services.</strong> We use information we automatically collect or generate about you when you use the Services, as well as information about your device model and operating system to analyze the use of and improve our Services.
              </li>
              <li>
                <strong>AI/Model Training.</strong> Currently, we are not training our proprietary artificial intelligence technology on de-identified audio recordings or transcriptions. In the future, we may do so to provide more accurate services, which may contain Personal Information.
              </li>
              <li>
                <strong>Communication and Marketing.</strong> We use your contact information to send emails regarding forgotten passwords or confirmation of payment. Currently, we do not send emails with news or updates pertaining to our Services.
              </li>
              <li>
                <strong>Prevent Fraud, Defend NoteForge Against Legal Claims or Disputes, Enforce Our Terms, and Comply with Our Legal Obligations.</strong> It is in our legitimate interest to protect our interests by (1) monitoring the use of the Services to detect fraud or any other user behavior which prejudices the integrity of our Services, including attempts to steal or unauthorized access to data, (2) taking steps to remedy aforementioned fraud and behavior, such as blocking or suspending accounts, (3) defending ourselves against legal claims or disputes, and (4) enforcing our terms and policies. When doing so, we will process the Personal Information relevant in such a case, including information you provide us.
              </li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>3. How We Use Cookies and Similar Technologies</h2>
            <p>
              We use Cookies to collect information about your browsing activities and to distinguish you from other users of our Services in order to aid your experience. Cookies are small files of letters and numbers that we store on your browser or on your device. They contain information that is transferred to your device.
            </p>
            <p>We use the following types of Cookies and similar technologies:</p>
            <ul className="bullet-list">
              <li>
                <strong>Strictly Necessary Cookies.</strong> Some Cookies are strictly necessary to make our Services available to you; for example, to provide login functionality, user authentication, and security. We cannot provide you with the Services without this type of Cookie.
              </li>
              <li>
                <strong>Functional Cookies.</strong> These are used to recognize you when you return to our Website. This enables us to personalize our content for you and remember your preferences.
              </li>
            </ul>
            <p>
              Currently, we do not use Cookies for measuring advertising effectiveness, analytical/performance purposes, or advertising.
            </p>
            <p>
              You can block Cookies by setting your internet browser to block all Cookies. However, if you use your browser settings to block all Cookies (including strictly necessary Cookies) you may not be able to use our Services.
            </p>
          </section>

          <section className="privacy-section">
            <h2>4. With Whom We Share Your Personal Information</h2>
            <p>We share your Personal Information with selected third parties as described below. The data is controlled by NoteForge.</p>
            <ul className="bullet-list">
              <li>
                <strong>Other Users.</strong> Other users who see your Personal Information (such as your username and email) and any other information you choose to share with them through the Services.
              </li>
              <li>
                <strong>Cloud Service Providers.</strong> Providers who we rely on for compute and data storage, including Amazon Web Services, based in the United States.
              </li>
              <li>
                <strong>Platform Support Providers.</strong> Providers who help us manage and monitor the Services, including Amplitude, which is based in the U.S. and provides user event data for our Services.
              </li>
              <li>
                <strong>Artificial Intelligence Service Providers.</strong> Providers that provide backend support for certain NoteForge product features.
              </li>
              <li>
                <strong>Payment Processors.</strong> Such as Stripe. These payment processors are responsible for the processing of your Personal Information, and may use your Personal Information for their own purposes in accordance with their privacy policies. More information is available here: <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer">stripe.com/gb/privacy</a>.
              </li>
            </ul>
            <p>
              Currently, we do not share with mobile advertising tracking providers, additional analytics providers, advertising partners, data labeling service providers, or providers of integrated third-party programs, apps, or platforms.
            </p>

            <h3>Legal Disclosure and Corporate Changes</h3>
            <ul className="bullet-list">
              <li>
                <strong>Law Enforcement Agencies, Public Authorities, or Other Judicial Bodies and Organizations.</strong> We disclose Personal Information if we are legally required to do so, or if we have a good faith belief that such use is reasonably necessary to comply with a legal obligation, process or request; enforce our terms of service and other agreements, policies, and standards, including investigation of any potential violation thereof; detect, prevent or otherwise address security, fraud or technical issues; or protect the rights, property or safety of us, our users, a third party or the public as required or permitted by law (including exchanging information with other companies and organizations for the purposes of fraud protection).
              </li>
              <li>
                <strong>Change of Corporate Ownership.</strong> If we are involved in a merger, acquisition, bankruptcy, reorganization, partnership, asset sale or other transaction, we may disclose your Personal Information as part of that transaction.
              </li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>5. How Long We Store Your Information</h2>
            <p>
              NoteForge stores all Personal Information for as long as necessary to fulfill the purposes set out in this Policy, or for as long as we are required to do so by law or in order to comply with a regulatory obligation. When deleting Personal Information, we will take measures to render such Personal Information irrecoverable or irreproducible, and the electronic files which contain Personal Information will be permanently deleted.
            </p>
            <p>
              We retain Personal Information for as long as necessary to provide our services and comply with legal obligations. Data is deleted or anonymized when it is no longer required.
            </p>

            <h3>Account Deletion</h3>
            <p>When you delete your account, we:</p>
            <ul className="bullet-list">
              <li>Disable access to the Services.</li>
              <li>Remove or anonymize direct identifiers such as name, email address, and phone number (e.g., by deleting the user and replacing identifiers with a random ID in place of username and email, ensuring the user cannot be identified at all).</li>
              <li>Prevent future identification of your account through the user profile. If you attempt to sign up or sign in again, you will be recognized and treated as a new user with a new user ID.</li>
            </ul>

            <h3>Retained Data</h3>
            <p>We may retain certain information where legally required or permitted, including:</p>
            <ul className="bullet-list">
              <li>Billing and transaction records (for tax, accounting, fraud prevention, and legal compliance).</li>
              <li>System logs and audit records.</li>
            </ul>
            <p>Such retained data is not used for marketing or profiling.</p>

            <h3>Anonymized Data</h3>
            <p>
              Content such as audio recordings or transcriptions may be retained in an anonymized or de-identified form so that it can no longer be associated with an identifiable individual. We do not attempt to re-identify anonymized data.
            </p>
          </section>

          <section className="privacy-section">
            <h2>6. Data Security</h2>
            <p>We implement physical, administrative, and technical safeguards to help us protect your Personal Information from unauthorized access, use, and disclosure. For example:</p>
            <ul className="bullet-list">
              <li>We use AWS S3 storage with Server Side Encryption (SSE) using AES-256.</li>
              <li>We send one-time passwords (OTPs) via email for user verification (we do not currently offer two-factor authentication).</li>
              <li>While we are not formally certified under ISO 27001/27002, we align our internal security practices with industry best practices, including access control, encryption, logging, and incident response, and rely on AWS's ISO 27001–certified infrastructure.</li>
              <li>We currently rely on AWS's SOC 2–certified infrastructure and follow industry-standard security practices.</li>
              <li>We require personnel to maintain the confidentiality of Personal Information.</li>
            </ul>
            <p>Despite these precautions, no platform is completely secure. We cannot guarantee the security of your Personal Information.</p>
            <p>In the event that we become aware of a security breach involving your Personal Information, we will provide notification in compliance with applicable law.</p>
          </section>

          <section className="privacy-section">
            <h2>7. Regulatory Compliance</h2>
            <p>
              We aim to comply with applicable data protection laws, including the General Data Protection Regulation (GDPR) in Europe and the California Consumer Privacy Act (CCPA). Under CCPA, we affirm that we do not sell personal data. We have implemented measures such as this privacy policy, data access and deletion requests, and data minimization, though we are not formally certified.
            </p>
            <p>
              The application is not designed to collect or process protected health information (PHI), and the Health Insurance Portability and Accountability Act (HIPAA) does not apply to our services.
            </p>
            <p>
              We are not currently certified under the EU-U.S. Data Privacy Framework (DPF) Principles. We rely on our cloud provider's compliance measures and contractual safeguards for international data transfers.
            </p>
          </section>

          <section className="privacy-section">
            <h2>8. Cross-Border Data Transfers</h2>
            <p>
              User data may be transferred to, stored, and processed in countries other than the user's country of residence, including where our cloud infrastructure is located (e.g., the United States).
            </p>
            <p>
              We use reputable cloud service providers such as Amazon Web Services (AWS) to host and process user data. Where personal data is transferred internationally, we rely on appropriate safeguards, including Standard Contractual Clauses and other contractual and technical protections provided by our service providers, as well as encryption and access controls, to protect user data.
            </p>
          </section>

          <section className="privacy-section">
            <h2>9. Your Rights</h2>
            <p>
              You have the right to request access to, correction of, or deletion of (erasure) your personal data, as well as to object to or request restriction of certain processing, and to request a copy of your personal data in a portable format, subject to applicable legal requirements and technical feasibility.
            </p>
            <p>
              We may require users to verify their identity before responding to a data rights request to protect against unauthorized access or deletion of personal data. Users can exercise their data protection rights by contacting us at <a href="mailto:support@noteforge.ai">support@noteforge.ai</a> or through in-app account settings where available.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10. Children's Privacy</h2>
            <p>
              Our Service is not directed to children under the age of 13, and we do not knowingly collect Personal Information from children under 13. If we become aware that we have inadvertently collected Personal Information from a child under 13, we will take steps to delete such information as soon as possible.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. Data Requests by Government</h2>
            <p>
              We do not disclose user data to law enforcement or government authorities unless we are legally required to do so. Requests must be supported by valid legal process, such as a subpoena, court order, or other lawful request. Where permitted by law, we will notify affected users before disclosing their information. We limit disclosures to the minimum data required to comply with the request.
            </p>
          </section>

          <section className="privacy-section">
            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time, for example due to changes in our operations or legal requirements. We will post the revised version here, with an updated revision date. We encourage you to review this page periodically.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
